import { useId, useState, type ReactNode } from 'react';

import { cn } from './cn';

/**
 * The show/hide control that sits inside a password field.
 *
 * **Why a wrapper and not a prop on one field component.** Four password
 * controls ship in this console and no two are built the same way — the auth
 * area's `Field`, the two rotate dialogs' hand-rolled inputs — so the reusable
 * part is the *box and the button*, not the input. This takes the input as a
 * render prop and hands it the `type` to use, which is the only thing revealing
 * actually changes.
 *
 * **It is a button, never a checkbox.** Revealing is an action taken and undone,
 * not a setting that persists: it resets on every mount, and nothing about it is
 * remembered between visits. `aria-pressed` carries the state, so the label can
 * stay one sentence instead of changing under the reader's cursor mid-read.
 *
 * The state lives here and dies here. A revealed password that survived a route
 * change would be a password left on screen by a component nobody is looking at.
 *
 * **The toggle sits at the reading end of the PAGE, not of the value.** This box
 * carries no direction of its own, so `inset-inline-end` resolves against the
 * console's `rtl` and the toggle lands on the left — where a reader of this
 * interface looks for the control belonging to the field they have just left.
 *
 * The space that clears it is the other half of one decision and cannot be read
 * apart from it. A password control is `dir="ltr"`, because it holds a machine
 * value, so the page's inline-END is that control's inline-START and the
 * reservation is `PASSWORD_REVEAL_PADDING` below. Two logical properties naming
 * one physical edge from two opposed frames: change either alone and the typed
 * value runs under the icon.
 */
export function PasswordReveal({
  children,
  showLabel = 'أظهر كلمة المرور',
  hideLabel = 'أخفِ كلمة المرور',
  className,
}: {
  /** Receives the type the control must render with — `password` or `text`. */
  children: (type: 'password' | 'text') => ReactNode;
  showLabel?: string;
  hideLabel?: string;
  className?: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const buttonId = useId();

  return (
    <div className={cn('relative', className)}>
      {children(revealed ? 'text' : 'password')}

      {/*
        `inset-inline-end` through the logical utility, never `left`: the toggle
        belongs at the reading end of the PAGE, which is the direction this box
        inherits. The width matches the padding the caller reserves on the
        control, so the typed value never runs underneath the icon.
      */}
      <button
        type="button"
        id={buttonId}
        onClick={() => setRevealed((current) => !current)}
        aria-pressed={revealed}
        aria-label={revealed ? hideLabel : showLabel}
        title={revealed ? hideLabel : showLabel}
        className={cn(
          'absolute inset-y-0 end-0 flex w-[2.75rem] items-center justify-center',
          'text-ink-muted transition-colors duration-fast ease-standard hover:text-ink',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent',
        )}
      >
        {revealed ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

/**
 * The padding a control must carry so its value clears the toggle.
 *
 * `inline-start`, not `inline-end`, and the asymmetry is the point: the toggle is
 * placed in the page's frame and this is applied in the control's, and a password
 * control reads `ltr` inside an `rtl` page. The two keywords name the same
 * physical edge precisely because the two frames are opposed.
 */
export const PASSWORD_REVEAL_PADDING = 'ps-[2.75rem]';

function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 4l16 16" />
      <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.3 4.1" />
      <path d="M6.6 7.9A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.7 9.7 0 0 0 3.5-.65" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}
